import { lazy, Suspense, useEffect } from 'react'
import { createBrowserRouter, Navigate, Outlet, useLocation, useParams } from 'react-router-dom'

import { DialogProvider } from '@hapana/shared/web/components/shared/dialog/dialog.provider'
import NavigationGuard from '_components/navigation-guard'
import CatchAllRoute from '_pages/catch-all-route/catch-all-route.component'
import MigrationGuard from '_components/migration-guard/migration-guard.component'
import SettingsGuard from '_components/settings-guard'
import SiteUrlSync from '_components/site-url-sync'
import SupportAssistantShell from '_components/support-assistant/support-assistant-shell'
import { SupportAssistantProvider } from '_components/support-assistant/support-assistant.context'
import { UpgradeRequestBridge } from '_components/support-assistant/upgrade-request-bridge'
import KBarProviderComponent from '_components/kbar/kbar-provider.component'
import { EntitlementsProvider } from '_providers/entitlements.provider'
import { FeatureGatedRoute } from '_components/feature-gate/feature-gated-route.component'
import { FeatureKeys } from '@hapana/shared/schemas/entitlements/feature-key.schema'
import { PageSkeleton } from '_components/page-skeletons'
import { hasStoredCoreSession } from '_utils/stored-session.util'
import { FeatureFlagKeys } from '@hapana/shared/constants/feature-flag.constants'
import { useAwaitedFeatureFlag } from '_hooks/use-awaited-feature-flag.hooks'
import { useFeatureFlag } from '_hooks/useFeatureFlag'
import { usePermissions } from '_stores/permissions.store'
import { ROUTES } from './app.constants'
import withAccessControl from '_components/hoc/with-access-control.hoc'
import { COVER_BOARD_PERMISSIONS } from '_pages/covers/covers.constants'

function HelpDefaultRedirect() {
  const VALID_TABS = ['search', 'chats', 'support', 'docs', 'activity']
  const lastTab = localStorage.getItem('hapana-help-last-tab')
  const tab = VALID_TABS.includes(lastTab) ? lastTab : 'search'
  return <Navigate to={tab} replace />
}

// Critical path components - loaded immediately
import Layout from '_pages/layout/layout.component'
import Login from '_pages/login/login.component'
import LoginAs from '_pages/login-as/login-as.component'
import RequireAuth from '_pages/require-route-auth/require-route-auth.component'
import RootErrorBoundary from '_pages/root-error-boundary/root-error-boundary.component'
import { IS_PLAN_SELF_SERVICE_ENABLED } from '_pages/control-center/plan-and-addons/plan-and-addons.constants'
import { RETAIL_GENERAL_SETTINGS_TAB } from '_pages/control-center/retail-general-settings/retail-general-settings.constants'
import BrandTaxSettingsRouteGuard from '_pages/control-center/retail-general-settings/brand-tax-settings-route-guard.component'
import ClassCoversBrandRouteGuard from '_pages/control-center/class-covers/class-covers-route-guard.component'
import FamilySettingsRouteGuard from '_pages/control-center/family-settings/family-settings-route-guard.component'
import ClientProfilesRouteGuard from '_pages/control-center/client-profiles/client-profiles-route-guard.component'
import { BRAND_SCHEDULE_SETTINGS_TAB } from '_pages/control-center/scheduler-settings/brand-schedule-settings.constants'
import BrandScheduleSettingsRouteGuard from '_pages/control-center/scheduler-settings/brand-schedule-settings-route-guard.component'
import { BRAND_RETAIL_SALES_SETTINGS_TAB } from '_pages/control-center/retail-sales-settings/brand-retail-sales-settings.constants'
import BrandRetailSalesSettingsRouteGuard from '_pages/control-center/retail-sales-settings/brand-retail-sales-settings-route-guard.component'

// Lazy-loaded page components - loaded on demand
const loadDashboard = () => import('_pages/dashboard/dashboard.component')
const Dashboard = lazy(loadDashboard)
const SpannerDashboard = lazy(() => import('_pages/dashboard/spanner-dashboard.component'))
const loadClients = () => import('_pages/clients/clients.component')
const Clients = lazy(loadClients)
const PlanAndAddons = lazy(
  () => import('_pages/control-center/plan-and-addons/plan-and-addons.component')
)
const ComparePlansPage = lazy(
  () => import('_pages/control-center/plan-and-addons/compare-plans-page.component')
)
const loadClientOverview = () => import('_pages/clients/client-overview.component')
const ClientOverview = lazy(loadClientOverview)
const CorporateLookup = lazy(
  () => import('_pages/clients/components/corporate-lookup/corporate-lookup.component')
)
const UserAccess = lazy(() => import('_pages/user-access/user-access.component'))
const UserProfile = lazy(() => import('_pages/user-profile/user-profile.component'))
const SiteTeamAccess = lazy(() => import('_pages/site-team-access/site-team-access.component'))
const LocationSelection = lazy(
  () => import('_pages/location-selection/location-selection.component')
)
const loadSchedule = () => import('_pages/schedule/schedule.component')
const Schedule = lazy(loadSchedule)
const Payments = lazy(() => import('_pages/payments/payments.component'))
const Reports = lazy(() => import('_pages/reports/reports.component'))
const MemberView = lazy(() => import('_pages/member-mirror/member-mirror.page'))
const ControlCenter = lazy(() => import('_pages/control-center/control-center.component'))
const Security = lazy(() => import('_pages/control-center/security/security.component'))
const MemberMovement = lazy(
  () => import('_pages/control-center/member-movement/member-movement.component')
)
const BrandCommissions = lazy(
  () => import('_pages/control-center/commissions/brand-commissions.component')
)
const ClassCoversBrandSettings = lazy(
  () => import('_pages/control-center/class-covers/class-covers.component')
)
const FamilySettings = lazy(
  () => import('_pages/control-center/family-settings/family-settings.component')
)
const ClientProfiles = lazy(
  () => import('_pages/control-center/client-profiles/client-profiles.component')
)
const Settings = lazy(() => import('_pages/settings/settings.component'))
// OC-759: location-level Audit History route disabled until the log is reworked.
// const SiteAuditHistory = lazy(
//   () => import('_pages/settings/site-audit-history/site-audit-history.component')
// )
const LocationSettings = lazy(() => import('_pages/location-settings/location-settings.component'))
const RoomResourceConfiguration = lazy(() =>
  import('_pages/settings/location-settings/location-configuration/components/business-locations-config/components/room-resource-configuration.component').then(
    (m) => ({ default: m.RoomResourceConfiguration })
  )
)
const SchedulerSettings = lazy(
  () => import('_pages/scheduler-settings/scheduler-settings.component.jsx')
)
const RetailSettings = lazy(() => import('_pages/retail-settings/retail-settings.component'))
const ZapierConnect = lazy(() => import('_pages/zapier-connect/zapier-connect.component'))
const SiteCommunicationTemplates = lazy(
  () =>
    import('_pages/location-communication-templates/components/templates/communication-templates/site-communication-templates.component')
)
const AccountSettings = lazy(() => import('_pages/account-settings/account-settings.component'))
const WellnessSettings = lazy(() => import('_pages/wellness-settings/wellness-settings.component'))
const CommunicationTemplates = lazy(
  () => import('_pages/communication-templates/communication-templates.component')
)
const FAQs = lazy(() => import('_pages/faqs/faqs.component'))
const RetailGeneralSettings = lazy(
  () => import('_pages/control-center/retail-general-settings/retail-general-settings.component')
)
const BrandScheduleSettings = lazy(
  () => import('_pages/control-center/scheduler-settings/brand-schedule-settings.component')
)
const BrandRetailSalesSettings = lazy(
  () => import('_pages/control-center/retail-sales-settings/brand-retail-sales-settings.component')
)
const BrandSessionGroupFormPage = lazy(
  () =>
    import('_pages/control-center/scheduler-settings/session-groups/session-group-form-page.component')
)
const BrandAddOnFormPage = lazy(
  () =>
    import('_pages/control-center/retail-sales-settings/add-ons/corporate-add-on-form-page.component')
)
const Integrations = lazy(() => import('_pages/integrations/integrations.component'))
const ApiIntegrations = lazy(
  () => import('_pages/control-center/api-integrations/api-integrations.component')
)
const Marketplace = lazy(() => import('_pages/control-center/marketplace/marketplace.component'))
const MarketplaceProviderDetail = lazy(
  () => import('_pages/control-center/marketplace/provider-detail-dispatcher.component')
)
const AuditHistory = lazy(
  () => import('_pages/control-center/audit-history/audit-history.component')
)
const Onboarding = lazy(() => import('_pages/control-center/onboarding/onboarding.component'))
const MigrationOverview = lazy(() => import('_pages/control-center/onboarding/MigrationOverview'))
const Setup = lazy(() => import('_pages/setup'))
const MobileAppPage = lazy(() => import('_pages/member-experiences/mobile-app/MobileAppPage'))
const EmbedSetup = lazy(() => import('_pages/embed-setup/index.tsx'))
const KioskSetup = lazy(() => import('_pages/kiosk-setup/index.tsx'))
const GeneralSettingsContent = lazy(
  () => import('_pages/general-settings/general-settings-content')
)
const DeeplinksComingSoon = lazy(() => import('_pages/member-experiences/deeplinks/index'))
lazy(() => import('_pages/setup/KioskPlaceholder'))
const EventTracker = lazy(() => import('_pages/event-tracker/event-tracker.component'))
const DynamicLoaderDemo = lazy(
  () => import('_pages/dynamic-loader-demo/dynamic-loader-demo.component')
)
const ValentineTest = lazy(() => import('_pages/valentine-test'))
const HelpDocs = lazy(() => import('_pages/help-docs/help-docs.component'))
const UpdatesPage = lazy(() => import('_pages/updates/updates.page'))
const ReleaseNotePage = lazy(() => import('_pages/updates/release-note.page'))
const HelpDocPage = lazy(() => import('_pages/updates/help-doc.page'))
const Search = lazy(() => import('_pages/search/search.component'))
const Capital = lazy(() => import('_pages/capital/capital.component'))
const Covers = withAccessControl(
  lazy(() => import('_pages/covers/covers.component')),
  { permissions: COVER_BOARD_PERMISSIONS }
)
const CoverQueueRedirect = withAccessControl(
  lazy(() => import('_pages/covers/queue/cover-queue.component')),
  { permissions: COVER_BOARD_PERMISSIONS }
)
const CoverClaim = lazy(() => import('_pages/cover-claim/cover-claim.component'))
const AssistantLayout = lazy(() => import('_pages/assistant/assistant-layout.component'))
const AssistantChatPage = lazy(() => import('_pages/assistant/assistant-chat-page.component'))
const SupportPage = lazy(() => import('_pages/assistant/support-page.component'))
const DocsPage = lazy(() => import('_pages/assistant/docs-page.component'))
const ActivityPage = lazy(() => import('_pages/assistant/activity-page.component'))
const ErrorBoundaryTest = lazy(
  () => import('_pages/error-boundary-test/error-boundary-test.component')
)
const loadRetail = () => import('_pages/retail/retail.component')
const Retail = lazy(loadRetail)
const loadNewSale = () => import('_pages/retail-settings/new-sale/new-sale.component')
const NewSale = lazy(loadNewSale)
const Checkout = lazy(() => import('_pages/retail-settings/checkout/checkout.component'))
const PaymentDetail = lazy(
  () => import('_pages/retail/components/payment-detail/payment-detail.component')
)
const Invoice = lazy(() => import('_pages/retail/components/invoice/invoice.component'))
const RecurringPaymentDetail = lazy(
  () =>
    import('_pages/retail/components/recurring-payment-detail/recurring-payment-detail.component')
)
const FailedPayments = lazy(() => import('_pages/retail/components/failed-payments'))
const DraftInvoices = lazy(() => import('_pages/retail/components/draft-invoices'))
const CashDrawer = lazy(() => import('_pages/retail/components/cash-drawer'))
const MemberMovementLocation = lazy(
  () => import('_pages/retail/components/member-movement/member-movement-location.component')
)
const AcceptInvitation = lazy(() => import('_pages/accept-invitation/accept-invitation.component'))
const SelectBrand = lazy(() => import('_pages/select-brand/select-brand.component'))
const WaitlistConfirm = lazy(() => import('_pages/waitlist-confirm/waitlist-confirm.component'))
const PublicInvoice = lazy(() => import('_pages/public-invoice/public-invoice.component'))
const PublicAgreement = lazy(() => import('_pages/public-agreement/public-agreement.component'))
const PublicFormFill = lazy(() => import('_pages/public-form-fill/public-form-fill.component'))
const PublicPaymentApproval = lazy(
  () => import('_pages/public-payment-approval/public-payment-approval.component')
)
const BarcodeCheckIn = lazy(() => import('_pages/barcode-checkin/barcode-checkin.component'))
const PublicAgreementV2 = lazy(
  () => import('_pages/public-agreement-v2/public-agreement.component')
)
const PendingItemsPage = lazy(() => import('_pages/pending-items/pending-items.page'))
const PendingItemsReceiptPage = lazy(
  () => import('_pages/pending-items/pending-items-receipt.page')
)
const EquipmentComplianceDeepLink = lazy(
  () => import('_pages/reports/equipment-compliance/equipment-compliance-deep-link.component')
)
const ResetPasswordPage = lazy(() => import('_pages/reset-password/reset-password-page.component'))
const PosthogDebug = lazy(() => import('_pages/posthog-debug/posthog-debug.component'))
const DataImport = lazy(() => import('_pages/data-import/data-import.component'))
const ContentSuite = lazy(() => import('_pages/content-suite/content-suite.page'))
const ContentEditor = lazy(() => import('_pages/content-suite/editor/content-editor.page'))
const MediaLibrary = lazy(() => import('_pages/content-suite/media/media-library.page'))
const ContentSuiteAnalytics = lazy(
  () => import('_pages/content-suite/analytics/content-analytics.page')
)
const FCMDebug = lazy(() => import('_pages/fcm-debug/fcm-debug.component'))

// Demo pages (public, no auth required)
const DemoSignup = lazy(() => import('_pages/demo/demo-signup.component'))
const DemoVerify = lazy(() => import('_pages/demo/demo-verify.component'))
const DemoCreating = lazy(() => import('_pages/demo/demo-creating.component'))
const DemoMagicLinkCallback = lazy(() => import('_pages/demo/demo-magic-link-callback.component'))

const MAIN_ROUTE_LOADERS = [
  loadDashboard,
  loadSchedule,
  loadClients,
  loadClientOverview,
  loadRetail,
  loadNewSale,
]

// The screens staff move between all day are fetched once the first page is idle, so switching
// between them never waits on a chunk download (INT-1539).
const prefetchMainRoutes = () => {
  for (const load of MAIN_ROUTE_LOADERS) {
    void load()
  }
}

const PageLoader = () => (hasStoredCoreSession() ? <PageSkeleton /> : null)

// Helper to wrap lazy components with Suspense
// Shows a loading spinner while chunks are fetched (important on slow networks / Windows antivirus scanning)
// The page itself handles its own loading states for data
const withSuspense = (Component) => (
  <Suspense fallback={<PageLoader />}>
    <Component />
  </Suspense>
)

export const ROOT_PATH = ''

const KbLinksGate = () => {
  const location = useLocation()
  const { value: isEnabled, isLoading } = useFeatureFlag(FeatureFlagKeys.DynamicKbLinksEnabled, {
    defaultValue: false,
  })

  if (isLoading) {
    return null
  }
  if (isEnabled) {
    return <Outlet />
  }

  // Flag off falls back to the brand-level route rather than the login bounce: an authenticated
  // user sent to /login is redirected straight back here by ?redirect=, which would loop.
  return <Navigate to={`/c${location.pathname}`} replace />
}

const AppLayout = () => {
  useEffect(() => {
    if ('requestIdleCallback' in window) {
      const handle = window.requestIdleCallback(prefetchMainRoutes)
      return () => window.cancelIdleCallback(handle)
    }
    const timeout = setTimeout(prefetchMainRoutes, 2000)
    return () => clearTimeout(timeout)
  }, [])

  return (
    <SupportAssistantProvider>
      {/* Inside the support provider (it needs the sidebar setters) and above everything that can
          render a lock, so route gates, control-centre tiles and the sidebar nav all raise the
          same pre-filled upgrade request. */}
      <UpgradeRequestBridge>
        <SupportAssistantShell>
          <KBarProviderComponent>
            <DialogProvider>
              <EntitlementsProvider>
                <Layout />
              </EntitlementsProvider>
            </DialogProvider>
          </KBarProviderComponent>
        </SupportAssistantShell>
      </UpgradeRequestBridge>
    </SupportAssistantProvider>
  )
}

const LANDING_PAGE_ROUTES = {
  index: true,
  element: withSuspense(Dashboard),
}

const USER_ACCESS_ROUTES = {
  path: ROUTES.userAccess.path,
  element: <Navigate to="users" replace />,
}

const USER_ACCESS_TABS_ROUTES = {
  path: ROUTES.userAccess.children.userAccessTabs.path,
  element: withSuspense(UserAccess),
}

const SITE_TEAM_ACCESS_ROUTES = {
  path: ROUTES.siteTeamAccess.path,
  element: withSuspense(SiteTeamAccess),
}

const USER_PROFILE_ROUTES = {
  path: ROUTES.userProfile.path,
  element: withSuspense(UserProfile),
}

const LOCATION_SELECTION_ROUTES = {
  path: ROUTES.locationSelection.path,
  element: withSuspense(LocationSelection),
}

const MOBILE_SETUP_ROUTES = {
  path: ROUTES.mobileSetup.path,
  element: <Navigate to="/c/control-hub/member-experiences/mobile-app" replace />,
}

const CLIENTS_ROUTES = {
  path: ROUTES.clients.path,
  element: withSuspense(Clients),
}

// Remount the profile when the :clientId param changes so client→client navigation
// (relations, timeline actors, shared-package owner links) always loads fresh data —
// the page's data hooks do not refetch on an in-place param change.
const ClientOverviewKeyed = () => {
  const { clientId } = useParams()
  return <ClientOverview key={clientId} />
}

const CLIENT_OVERVIEW_ROUTES = {
  path: ROUTES.clients.children.clientOverview.path,
  element: withSuspense(ClientOverviewKeyed),
}

const RETAIL_ROUTES = {
  path: ROUTES.retail.path,
  element: withSuspense(Retail),
  children: [
    {
      index: true,
      element: <Navigate to="payment-log" replace />,
    },
    {
      path: 'payment-log',
      element: withSuspense(Retail),
    },
    {
      path: 'memberships',
      element: withSuspense(Retail),
    },
    {
      path: 'packages',
      element: withSuspense(Retail),
    },
    {
      path: 'scheduled',
      element: withSuspense(Retail),
    },
    {
      path: 'pos',
      element: withSuspense(Retail),
    },
    {
      path: 'gift-card',
      element: withSuspense(Retail),
    },
    {
      path: 'disputes',
      element: withSuspense(Retail),
    },
    {
      path: ROUTES.retailDashboard.children.cancelledInvoices.resourcePath,
      element: withSuspense(Retail),
    },
    {
      path: 'pay-as-you-go',
      element: withSuspense(Retail),
    },
  ],
}

const SCHEDULE_ROUTES = {
  path: ROUTES.schedule.path,
  element: withSuspense(Schedule),
}

const PAYMENTS_ROUTES = {
  path: ROUTES.payments.path,
  element: withSuspense(Payments),
}

const REPORTS_ROUTES = {
  path: ROUTES.reports.path,
  element: withSuspense(Reports),
}

const MEMBER_VIEW_ROUTES = {
  path: ROUTES.memberView.path,
  element: withSuspense(MemberView),
}

const CORPORATE_LOOKUP_ROUTES = {
  path: ROUTES.clients.children.lookup.path,
  element: withSuspense(CorporateLookup),
}

const CONTROL_CENTER_ROUTES = {
  path: ROUTES.controlCenter.path,
  element: withSuspense(ControlCenter),
}

const SETTINGS_ROUTES = {
  path: ROUTES.settings.path,
  element: withSuspense(Settings),
}

const LOCATION_SETTINGS_ROUTES = {
  path: ROUTES.settings.children.locationSettings.path,
  element: withSuspense(LocationSettings),
}

const LOCATION_SETTINGS_TABS_ROUTES = {
  path: ROUTES.settings.children.locationSettings.children.locationSettingsTabs.path,
  element: withSuspense(LocationSettings),
}

const ROOM_RESOURCE_CONFIGURATION_ROUTES = {
  path: ROUTES.settings.children.locationSettings.children.roomResourceConfiguration.path,
  element: withSuspense(RoomResourceConfiguration),
}
const LOCATION_COMMUNICATION_TEMPLATES_ROUTES = {
  path: ROUTES.settings.children.locationSettings.children.siteCommunicationTemplates.path,
  element: withSuspense(SiteCommunicationTemplates),
}

const SCHEDULER_SETTINGS_ROUTES = {
  path: ROUTES.settings.children.schedulerSettings.path,
  element: withSuspense(SchedulerSettings),
}

const SCHEDULER_SETTINGS_TABS_ROUTES = {
  path: ROUTES.settings.children.schedulerSettings.children.schedulerSettingsTabs.path,
  element: withSuspense(SchedulerSettings),
}

const RETAIL_SETTINGS_ROUTES = {
  path: ROUTES.settings.children.retailSettings.path,
  element: withSuspense(RetailSettings),
}

const RETAIL_SETTINGS_TABS_ROUTES = {
  path: ROUTES.settings.children.retailSettings.children.retailSettingsTabs.path,
  element: withSuspense(RetailSettings),
}

const NEW_SALE_ROUTES = {
  path: ROUTES.retailDashboard.children.newSale.path,
  element: withSuspense(NewSale),
}

const NEW_SALE_TABS_ROUTES = {
  path: ROUTES.retailDashboard.children.newSale.children.newSaleTabs.path,
  element: withSuspense(NewSale),
}

const CHECKOUT_ROUTES = {
  path: ROUTES.retailDashboard.children.checkout.path,
  element: withSuspense(Checkout),
}

const PAYMENT_DETAIL_ROUTES = {
  path: ROUTES.retailDashboard.children.paymentDetail.path,
  element: withSuspense(PaymentDetail),
}

const RECURRING_PAYMENT_DETAIL_ROUTES = {
  path: ROUTES.retailDashboard.children.recurringPaymentDetail.path,
  element: withSuspense(RecurringPaymentDetail),
}

const INVOICE_ROUTES = {
  path: ROUTES.retailDashboard.children.invoice.path,
  element: withSuspense(Invoice),
}

const FAILED_PAYMENTS_ROUTES = {
  path: ROUTES.retailDashboard.children.failed.path,
  element: withSuspense(FailedPayments),
}

const DRAFT_INVOICES_ROUTES = {
  path: ROUTES.retailDashboard.children.draftInvoices.path,
  element: withSuspense(DraftInvoices),
}

const CASH_DRAWER_ROUTES = {
  path: ROUTES.retailDashboard.children.cashDrawer.path,
  element: withSuspense(CashDrawer),
}

const MEMBER_MOVEMENT_LOCATION_ROUTES = {
  path: ROUTES.retailDashboard.children.memberMovement.routePattern,
  element: withSuspense(MemberMovementLocation),
}

const ACCOUNT_SETTINGS_ROUTES = {
  path: ROUTES.settings.children.accountSettings.path,
  element: withSuspense(AccountSettings),
}

const ACCOUNT_SETTINGS_TABS_ROUTES = {
  path: ROUTES.settings.children.accountSettings.children.accountSettingsTabs.path,
  element: withSuspense(AccountSettings),
}

const WELLNESS_SETTINGS_ROUTES = {
  path: ROUTES.settings.children.wellnessSettings.path,
  element: withSuspense(WellnessSettings),
}

const WELLNESS_SETTINGS_TABS_ROUTES = {
  path: ROUTES.settings.children.wellnessSettings.children.wellnessSettingsTabs.path,
  element: withSuspense(WellnessSettings),
}

// OC-759: see the commented-out lazy import above.
// const SITE_AUDIT_HISTORY_ROUTES = {
//   path: ROUTES.settings.children.auditHistorySettings.path,
//   element: withSuspense(SiteAuditHistory),
// }

const COMMUNICATION_TEMPLATES_ROUTES = {
  path: ROUTES.controlCenter.children.communicationTemplates.path,
  element: withSuspense(CommunicationTemplates),
}

const FAQS_ROUTES = {
  path: ROUTES.controlCenter.children.faqs.path,
  element: withSuspense(FAQs),
}

// OC-320: self-service plan management is built but not yet signed off, so these routes are closed.
// Hiding the Control Centre card alone would still leave the URL reachable, so the routes redirect
// back to Control Centre. `IS_PLAN_SELF_SERVICE_ENABLED` is the single switch — no flag to resolve,
// so there is no loading state to wait on.
function PlanSelfServiceRouteGuard({ children }) {
  if (!IS_PLAN_SELF_SERVICE_ENABLED) {
    return <Navigate to={`/c/${ROUTES.controlCenter.path}`} replace />
  }
  return children
}

// OC-554: the Control Centre card is filtered by the same flag, so without this the module stays
// reachable by direct link once the flag is switched off — Member Movement could not be dark-
// launched or rolled back without a deploy. `member_movement__view` and the backend entitlement
// still apply on top of this.
function MemberMovementRouteGuard({ children }) {
  const isEnabled = useAwaitedFeatureFlag(FeatureFlagKeys.MemberMovementEnabled)
  if (isEnabled === null) {
    return null
  }
  if (!isEnabled) {
    return <Navigate to={`/c/${ROUTES.controlCenter.path}`} replace />
  }
  return children
}

// OC-1721: the Control Hub card is filtered by the same flag and permission, so this keeps the
// brand commission defaults unreachable by direct link when either is missing.
function BrandCommissionsRouteGuard({ children }) {
  const isEnabled = useAwaitedFeatureFlag(FeatureFlagKeys.CommissionsEnabled)
  const { hasPermission, isLoading: isPermissionsLoading } = usePermissions()
  if (isEnabled === null || isPermissionsLoading) {
    return null
  }
  if (isEnabled !== true || !hasPermission('corporate__commissions__manage')) {
    return <Navigate to={`/c/${ROUTES.controlCenter.path}`} replace />
  }
  return children
}

// Native class covers: the module stays unreachable by direct link while the flag is off, so it can
// be dark-launched and rolled back without a deploy. The covers permissions and the backend flag
// gate still apply on top of this.
function ClassCoversRouteGuard({ children }) {
  const isEnabled = useAwaitedFeatureFlag(FeatureFlagKeys.ClassCoversEnabled)
  if (isEnabled === null) {
    return null
  }
  if (!isEnabled) {
    return <Navigate to={`/${ROUTES.dashboard.path}`} replace />
  }
  return children
}

const BRAND_COMMISSIONS_ROUTES = {
  path: ROUTES.controlCenter.children.commissions.path,
  element: (
    <BrandCommissionsRouteGuard>{withSuspense(BrandCommissions)}</BrandCommissionsRouteGuard>
  ),
}

const COVERS_ROUTES = {
  path: ROUTES.covers.path,
  element: withSuspense(Covers),
}

const COVER_QUEUE_REDIRECT_ROUTES = {
  path: ROUTES.covers.children.queue.path,
  element: withSuspense(CoverQueueRedirect),
}

// Not entitlement-gated as a whole: a location whose plan lost class covers must still reach its
// existing covers to cancel or withdraw them. The page gates new requests and claims instead.
const CLASS_COVERS_GATE = (
  <ClassCoversRouteGuard>
    <Outlet />
  </ClassCoversRouteGuard>
)

const SITE_CLASS_COVERS_ROUTES = {
  element: CLASS_COVERS_GATE,
  children: [COVERS_ROUTES, COVER_QUEUE_REDIRECT_ROUTES],
}

const COVER_CLAIM_ROUTES = {
  path: ROUTES.coverClaim.path,
  element: withSuspense(CoverClaim),
}

const RETAIL_GENERAL_SETTINGS_ROUTES = {
  path: ROUTES.controlCenter.children.retailGeneralSettings.path,
  element: (
    <BrandTaxSettingsRouteGuard>
      <Navigate to={RETAIL_GENERAL_SETTINGS_TAB.ACCOUNTS_AND_CREDITS.path} replace />
    </BrandTaxSettingsRouteGuard>
  ),
}

const RETAIL_GENERAL_SETTINGS_TABS_ROUTES = {
  path: ROUTES.controlCenter.children.retailGeneralSettings.children.retailGeneralSettingsTabs.path,
  element: (
    <BrandTaxSettingsRouteGuard>{withSuspense(RetailGeneralSettings)}</BrandTaxSettingsRouteGuard>
  ),
}

const BRAND_SCHEDULE_SETTINGS_ROUTES = {
  path: ROUTES.controlCenter.children.scheduler.path,
  element: (
    <BrandScheduleSettingsRouteGuard>
      <Navigate to={BRAND_SCHEDULE_SETTINGS_TAB.SESSION_GROUPS.path} replace />
    </BrandScheduleSettingsRouteGuard>
  ),
}

const BRAND_SCHEDULE_SETTINGS_TABS_ROUTES = {
  path: ROUTES.controlCenter.children.scheduler.children.schedulerTabs.path,
  element: (
    <BrandScheduleSettingsRouteGuard>
      {withSuspense(BrandScheduleSettings)}
    </BrandScheduleSettingsRouteGuard>
  ),
}

const BRAND_NEW_SESSION_GROUP_ROUTES = {
  path: ROUTES.controlCenter.children.scheduler.children.newSessionGroup.path,
  element: (
    <BrandScheduleSettingsRouteGuard>
      {withSuspense(BrandSessionGroupFormPage)}
    </BrandScheduleSettingsRouteGuard>
  ),
}

const BRAND_EDIT_SESSION_GROUP_ROUTES = {
  path: ROUTES.controlCenter.children.scheduler.children.editSessionGroup.path,
  element: (
    <BrandScheduleSettingsRouteGuard>
      {withSuspense(BrandSessionGroupFormPage)}
    </BrandScheduleSettingsRouteGuard>
  ),
}

const BRAND_RETAIL_SALES_SETTINGS_ROUTES = {
  element: (
    <BrandRetailSalesSettingsRouteGuard>
      <Outlet />
    </BrandRetailSalesSettingsRouteGuard>
  ),
  children: [
    {
      path: ROUTES.controlCenter.children.retailSalesSettings.path,
      element: <Navigate to={BRAND_RETAIL_SALES_SETTINGS_TAB.ADD_ONS.path} replace />,
    },
    {
      path: ROUTES.controlCenter.children.retailSalesSettings.children.retailSalesSettingsTabs.path,
      element: withSuspense(BrandRetailSalesSettings),
    },
    {
      // Only creating needs the plan, as on the server: a brand that downgraded with live add-ons
      // must still reach them to edit, archive or delete.
      element: <FeatureGatedRoute feature={FeatureKeys.BrandControls} />,
      children: [
        {
          path: ROUTES.controlCenter.children.retailSalesSettings.children.newAddOn.path,
          element: withSuspense(BrandAddOnFormPage),
        },
      ],
    },
    {
      path: ROUTES.controlCenter.children.retailSalesSettings.children.editAddOn.path,
      element: withSuspense(BrandAddOnFormPage),
    },
  ],
}

const PLAN_AND_ADDONS_ROUTES = {
  path: ROUTES.controlCenter.children.planAndAddons.path,
  element: <PlanSelfServiceRouteGuard>{withSuspense(PlanAndAddons)}</PlanSelfServiceRouteGuard>,
}

const COMPARE_PLANS_ROUTES = {
  path: ROUTES.controlCenter.children.planAndAddons.children.comparePlans.path,
  element: <PlanSelfServiceRouteGuard>{withSuspense(ComparePlansPage)}</PlanSelfServiceRouteGuard>,
}

const INTEGRATIONS_ROUTES = {
  path: ROUTES.controlCenter.children.integrations.path,
  element: withSuspense(Integrations),
}

const API_INTEGRATIONS_ROUTES = {
  path: ROUTES.controlCenter.children.apiIntegrations.path,
  // Developer APIs opens on the Overview tab.
  element: <Navigate to="overview" replace />,
}

// The page used to live at control-center/api-integrations with different tab names.
// Old links land on the matching tab of control-center/developer-apis.
const LEGACY_DEVELOPER_API_TABS = {
  api: 'member-api',
  'api-logging': 'logging',
  'api-keys': 'business-api?view=api-keys',
  webhooks: 'webhooks',
}

const LegacyApiIntegrationsRedirect = () => {
  const { tab } = useParams()
  const target = LEGACY_DEVELOPER_API_TABS[tab] ?? 'overview'
  return <Navigate to={`../../developer-apis/${target}`} relative="path" replace />
}

const LEGACY_API_INTEGRATIONS_ROUTES = {
  path: ROUTES.controlCenter.children.apiIntegrations.children.legacyApiIntegrations.path,
  element: <Navigate to="../developer-apis/overview" relative="path" replace />,
}

const LEGACY_API_INTEGRATIONS_TABS_ROUTES = {
  path: ROUTES.controlCenter.children.apiIntegrations.children.legacyApiIntegrationsTabs.path,
  element: <LegacyApiIntegrationsRedirect />,
}

const API_INTEGRATIONS_TABS_ROUTES = {
  path: ROUTES.controlCenter.children.apiIntegrations.children.apiIntegrationsTabs.path,
  element: withSuspense(ApiIntegrations),
}

const MARKETPLACE_ROUTES = {
  path: ROUTES.controlCenter.children.marketplace.path,
  element: withSuspense(Marketplace),
}

const MARKETPLACE_PROVIDER_DETAIL_ROUTES = {
  path: ROUTES.controlCenter.children.marketplace.children.providerDetail.path,
  element: withSuspense(MarketplaceProviderDetail),
}

const AUDIT_HISTORY_ROUTES = {
  path: ROUTES.controlCenter.children.auditHistory.path,
  element: withSuspense(AuditHistory),
}

// Migration is Hapana-staff-only (import-utility flag, targeted to hapana.com in PostHog).
// Guard the routes so customers can't reach it via direct deep-link, not just by hiding the tile.
const ONBOARDING_ROUTES = {
  path: ROUTES.controlCenter.children.onboarding.path,
  element: <MigrationGuard>{withSuspense(MigrationOverview)}</MigrationGuard>,
}

const MIGRATION_WIZARD_ROUTES = {
  path: ROUTES.controlCenter.children.onboarding.children.migrationWizard.path,
  element: <MigrationGuard>{withSuspense(Onboarding)}</MigrationGuard>,
}

const SELF_SERVICE_ROUTES = {
  path: ROUTES.controlCenter.children.selfService.path,
  element: withSuspense(Setup),
}

const SECURITY_ROUTES = {
  path: ROUTES.controlCenter.children.security.path,
  element: withSuspense(Security),
}

const FAMILY_SETTINGS_ROUTES = {
  path: ROUTES.controlCenter.children.familySettings.path,
  element: <FamilySettingsRouteGuard>{withSuspense(FamilySettings)}</FamilySettingsRouteGuard>,
}

const CLIENT_PROFILES_ROUTES = {
  path: ROUTES.controlCenter.children.clientProfiles.path,
  element: <ClientProfilesRouteGuard>{withSuspense(ClientProfiles)}</ClientProfilesRouteGuard>,
}

const SECURITY_TABS_ROUTES = {
  path: ROUTES.controlCenter.children.security.children.securityTabs.path,
  element: withSuspense(Security),
}

const MEMBER_MOVEMENT_ROUTES = {
  path: ROUTES.controlCenter.children.memberMovement.path,
  element: (
    <MemberMovementRouteGuard>
      <Navigate to="runs" replace />
    </MemberMovementRouteGuard>
  ),
}

const MEMBER_MOVEMENT_TABS_ROUTES = {
  path: ROUTES.controlCenter.children.memberMovement.children.memberMovementTabs.path,
  element: <MemberMovementRouteGuard>{withSuspense(MemberMovement)}</MemberMovementRouteGuard>,
}

const MEMBER_MOVEMENT_RUN_DETAIL_ROUTES = {
  path: ROUTES.controlCenter.children.memberMovement.children.memberMovementRunDetail.path,
  element: <MemberMovementRouteGuard>{withSuspense(MemberMovement)}</MemberMovementRouteGuard>,
}

const CLASS_COVERS_BRAND_ROUTES = {
  element: (
    <ClassCoversBrandRouteGuard>
      <FeatureGatedRoute feature={FeatureKeys.ClassCovers} />
    </ClassCoversBrandRouteGuard>
  ),
  children: [
    {
      path: ROUTES.controlCenter.children.classCovers.path,
      element: withSuspense(ClassCoversBrandSettings),
    },
  ],
}

const MEMBER_EXPERIENCES_ROUTES = {
  path: ROUTES.controlCenter.children.memberExperiences.path,
  element: withSuspense(Setup),
  children: [
    {
      index: true,
      element: <Navigate to="settings" replace />,
    },
    {
      path: 'settings',
      element: withSuspense(GeneralSettingsContent),
    },
    {
      path: 'deeplinks',
      element: withSuspense(DeeplinksComingSoon),
    },
    {
      path: 'mobile-app',
      element: withSuspense(MobileAppPage),
    },
    {
      path: 'embed',
      element: withSuspense(EmbedSetup),
    },
    {
      path: 'kiosk',
      element: withSuspense(KioskSetup),
    },
  ],
}

const EVENT_TRACKER_ROUTES = {
  path: ROUTES.product.children.eventTracker.path,
  element: withSuspense(EventTracker),
}

const DYNAMIC_LOADER_DEMO_ROUTES = {
  path: ROUTES.product.children.dynamicLoaderDemo.path,
  element: withSuspense(DynamicLoaderDemo),
}

const VALENTINE_TEST_ROUTES = {
  path: 'product/valentine-test',
  element: withSuspense(ValentineTest),
}

const HELP_DOCS_ROUTES = {
  path: ROUTES.helpDocs.path,
  element: withSuspense(HelpDocs),
}

const UPDATES_ROUTES = {
  path: ROUTES.updates.path,
  element: withSuspense(UpdatesPage),
}

const UPDATES_HELP_DOCS_ROUTES = {
  path: ROUTES.updates.children.helpDocs.path,
  element: withSuspense(UpdatesPage),
}

const UPDATES_HELP_DOC_ROUTES = {
  path: ROUTES.updates.children.helpDoc.path,
  element: withSuspense(HelpDocPage),
}

const UPDATES_NOTE_ROUTES = {
  path: ROUTES.updates.children.note.path,
  element: withSuspense(ReleaseNotePage),
}

const HELP_DOCS_ARTICLE_ROUTES = {
  path: ROUTES.helpDocs.children.article.path,
  element: withSuspense(HelpDocs),
}

const CONTENT_SUITE_ROUTES = {
  path: ROUTES.contentSuite.path,
  element: withSuspense(ContentSuite),
}

const CONTENT_SUITE_MEDIA_ROUTES = {
  path: ROUTES.contentSuite.children.mediaLibrary.path,
  element: withSuspense(MediaLibrary),
}

const CONTENT_SUITE_ANALYTICS_ROUTES = {
  path: ROUTES.contentSuite.children.analytics.path,
  element: withSuspense(ContentSuiteAnalytics),
}

const CONTENT_SUITE_NEW_ROUTES = {
  path: 'content-suite/new',
  element: withSuspense(ContentEditor),
}

const CONTENT_SUITE_ENTRY_ROUTES = {
  path: ROUTES.contentSuite.children.contentEntry.path,
  element: withSuspense(ContentEditor),
}

// Capital is a location-level surface — it renders financing for one site's Stripe connected
// account, so it is only mounted under /s/:siteId.
const CAPITAL_ROUTES = {
  path: ROUTES.capital.path,
  element: withSuspense(Capital),
}

const ASSISTANT_ROUTES = {
  path: ROUTES.assistant.path,
  element: withSuspense(AssistantLayout),
  children: [
    { index: true, element: <HelpDefaultRedirect /> },
    { path: 'search', element: withSuspense(Search) },
    { path: 'chats', element: withSuspense(AssistantChatPage) },
    { path: 'support', element: withSuspense(SupportPage) },
    { path: 'docs', element: withSuspense(DocsPage) },
    { path: 'activity', element: withSuspense(ActivityPage) },
  ],
}

const ACCEPT_INVITATION_ROUTES = {
  path: 'p/accept-invitation/:token',
  element: withSuspense(AcceptInvitation),
}

const WAITLIST_CONFIRM_ROUTES = {
  path: 'p/waitlist/:token',
  element: withSuspense(WaitlistConfirm),
}

const PUBLIC_INVOICE_ROUTES = {
  path: 'p/invoice/:token',
  element: withSuspense(PublicInvoice),
}

const PUBLIC_AGREEMENT_ROUTES = {
  path: 'p/agreement/:token',
  element: withSuspense(PublicAgreement),
}

const PUBLIC_PAYMENT_APPROVAL_ROUTES = {
  path: 'p/payment-approval/:token',
  element: withSuspense(PublicPaymentApproval),
}

const BARCODE_CHECK_IN_ROUTES = {
  path: 'p/barcode/:siteId',
  element: withSuspense(BarcodeCheckIn),
}

const PUBLIC_AGREEMENT_V2_ROUTES = {
  path: 'p/agreement-v2/:token',
  element: withSuspense(PublicAgreementV2),
}

const PUBLIC_FORM_FILL_ROUTES = {
  path: ROUTES.publicFormFill.path,
  element: withSuspense(PublicFormFill),
}

const PUBLIC_PENDING_ITEMS_ROUTES = [
  { path: ROUTES.publicPendingItems.path, element: withSuspense(PendingItemsPage) },
  {
    path: ROUTES.publicPendingItems.children.session.path,
    element: withSuspense(PendingItemsPage),
  },
  {
    path: ROUTES.publicPendingItems.children.receipt.path,
    element: withSuspense(PendingItemsReceiptPage),
  },
  {
    path: ROUTES.publicPendingItems.children.receiptSession.path,
    element: withSuspense(PendingItemsReceiptPage),
  },
]

const EQUIPMENT_COMPLIANCE_DEEP_LINK_ROUTES = {
  path: ROUTES.reports.children.equipmentCompliance.path,
  element: withSuspense(EquipmentComplianceDeepLink),
}

const ERROR_BOUNDARY_TEST_ROUTES = {
  path: 'error-boundary-test',
  element: withSuspense(ErrorBoundaryTest),
}

const APP_ROUTES = {
  path: '/',
  element: <AppLayout />,
  errorElement: <RootErrorBoundary />,
  children: [
    {
      path: 'login',
      element: <Login />,
    },
    {
      path: 'login-as',
      element: <LoginAs />,
    },
    {
      path: 'select-brand',
      element: withSuspense(SelectBrand),
    },
    {
      element: <RequireAuth />,
      children: [
        // FCM Debug — accessible without site selection
        { path: 'fcm-debug', element: withSuspense(FCMDebug) },

        {
          element: <NavigationGuard />,
          children: [
            // Zapier OAuth connect page — guard skipped via SKIP_GUARD_PATHS
            {
              path: ROUTES.zapierConnect.path,
              element: withSuspense(ZapierConnect),
            },

            // Landing page (Dashboard) - now protected
            LANDING_PAGE_ROUTES,

            // OC-1294: root-mounted so a brand-only link (/help-docs/:slug) resolves — the
            // NavigationGuard rewrites it to /s/:siteId/help-docs/:slug for site users.
            {
              element: <KbLinksGate />,
              children: [HELP_DOCS_ROUTES, HELP_DOCS_ARTICLE_ROUTES],
            },

            // PLG routes
            { path: 'import', element: withSuspense(DataImport) },

            // The evidence export email links here; it forwards to the location's report
            EQUIPMENT_COMPLIANCE_DEEP_LINK_ROUTES,

            // Utility routes
            USER_PROFILE_ROUTES,
            LOCATION_SELECTION_ROUTES,

            // Corporate-level routes - /c/*
            // These routes don't require a siteId (brand-wide functionality)
            {
              path: 'c',
              children: [
                // Dashboard as default/index route for corporate
                LANDING_PAGE_ROUTES,

                USER_PROFILE_ROUTES,
                USER_ACCESS_ROUTES,
                USER_ACCESS_TABS_ROUTES,
                CONTROL_CENTER_ROUTES,
                REPORTS_ROUTES,
                MEMBER_VIEW_ROUTES,
                COMMUNICATION_TEMPLATES_ROUTES,
                FAQS_ROUTES,
                PLAN_AND_ADDONS_ROUTES,
                COMPARE_PLANS_ROUTES,
                INTEGRATIONS_ROUTES,
                // OC-320: the Developer API (Member API) is Premium. Gate both the page and its tabs
                // so typing the URL is blocked, not just the Control Center card.
                {
                  element: <FeatureGatedRoute feature={FeatureKeys.ReadApi} />,
                  children: [
                    API_INTEGRATIONS_ROUTES,
                    API_INTEGRATIONS_TABS_ROUTES,
                    LEGACY_API_INTEGRATIONS_ROUTES,
                    LEGACY_API_INTEGRATIONS_TABS_ROUTES,
                  ],
                },
                // OC-320: the Marketplace surface is Premium — gate the list and provider detail on
                // the parent Marketplace feature (individual providers gate separately inside it).
                {
                  element: <FeatureGatedRoute feature={FeatureKeys.Marketplace} />,
                  children: [MARKETPLACE_ROUTES, MARKETPLACE_PROVIDER_DETAIL_ROUTES],
                },
                // OC-320: Audit History is Pro-only. The gate renders the upgrade screen for lower
                // tiers when the product-tiering flag is on; inert (renders normally) otherwise.
                {
                  element: <FeatureGatedRoute feature={FeatureKeys.FullAuditLogs} />,
                  children: [AUDIT_HISTORY_ROUTES],
                },
                ONBOARDING_ROUTES,
                MIGRATION_WIZARD_ROUTES,
                SELF_SERVICE_ROUTES,
                RETAIL_GENERAL_SETTINGS_ROUTES,
                RETAIL_GENERAL_SETTINGS_TABS_ROUTES,
                BRAND_SCHEDULE_SETTINGS_ROUTES,
                BRAND_SCHEDULE_SETTINGS_TABS_ROUTES,
                BRAND_NEW_SESSION_GROUP_ROUTES,
                BRAND_EDIT_SESSION_GROUP_ROUTES,
                BRAND_RETAIL_SALES_SETTINGS_ROUTES,
                {
                  element: <FeatureGatedRoute feature={FeatureKeys.Commissions} />,
                  children: [BRAND_COMMISSIONS_ROUTES],
                },
                SECURITY_ROUTES,
                SECURITY_TABS_ROUTES,
                FAMILY_SETTINGS_ROUTES,
                CLIENT_PROFILES_ROUTES,
                MEMBER_MOVEMENT_ROUTES,
                MEMBER_MOVEMENT_TABS_ROUTES,
                MEMBER_MOVEMENT_RUN_DETAIL_ROUTES,
                CLASS_COVERS_BRAND_ROUTES,
                EVENT_TRACKER_ROUTES,
                MEMBER_EXPERIENCES_ROUTES,
                { path: 'posthog', element: withSuspense(PosthogDebug) },
                // OC-320: Content Suite is Premium. Gate wraps the whole content-suite route group.
                {
                  element: <FeatureGatedRoute feature={FeatureKeys.ContentSuite} />,
                  children: [
                    CONTENT_SUITE_ROUTES,
                    CONTENT_SUITE_MEDIA_ROUTES,
                    CONTENT_SUITE_ANALYTICS_ROUTES,
                    CONTENT_SUITE_NEW_ROUTES,
                    CONTENT_SUITE_ENTRY_ROUTES,
                  ],
                },
                HELP_DOCS_ROUTES,
                HELP_DOCS_ARTICLE_ROUTES,
                UPDATES_ROUTES,
                UPDATES_HELP_DOCS_ROUTES,
                UPDATES_HELP_DOC_ROUTES,
                UPDATES_NOTE_ROUTES,
                ASSISTANT_ROUTES,
              ],
            },

            // Site-level routes - /s/:siteId/*
            // These routes require a specific siteId in the path
            {
              path: 's/:siteId',
              element: <SiteUrlSync />,
              children: [
                // Dashboard as default/index route for site
                LANDING_PAGE_ROUTES,

                // Temporary: Spanner-powered dashboard on /home-2
                { path: ROUTES.home2.path, element: withSuspense(SpannerDashboard) },

                // Utility routes accessible from site context
                USER_PROFILE_ROUTES,

                CLIENTS_ROUTES,
                CLIENT_OVERVIEW_ROUTES,
                CORPORATE_LOOKUP_ROUTES,
                MOBILE_SETUP_ROUTES,
                SITE_TEAM_ACCESS_ROUTES,
                SCHEDULE_ROUTES,
                SITE_CLASS_COVERS_ROUTES,
                PAYMENTS_ROUTES,
                REPORTS_ROUTES,
                MEMBER_VIEW_ROUTES,
                RETAIL_ROUTES,
                NEW_SALE_ROUTES,
                NEW_SALE_TABS_ROUTES,
                CHECKOUT_ROUTES,
                PAYMENT_DETAIL_ROUTES,
                RECURRING_PAYMENT_DETAIL_ROUTES,
                INVOICE_ROUTES,
                FAILED_PAYMENTS_ROUTES,
                DRAFT_INVOICES_ROUTES,
                CASH_DRAWER_ROUTES,
                {
                  element: <FeatureGatedRoute feature={FeatureKeys.MemberMovement} />,
                  children: [MEMBER_MOVEMENT_LOCATION_ROUTES],
                },
                { path: 'import', element: withSuspense(DataImport) },
                // OC-320: Content Suite is Premium (site-level tree).
                {
                  element: <FeatureGatedRoute feature={FeatureKeys.ContentSuite} />,
                  children: [
                    CONTENT_SUITE_ROUTES,
                    CONTENT_SUITE_MEDIA_ROUTES,
                    CONTENT_SUITE_ANALYTICS_ROUTES,
                    CONTENT_SUITE_NEW_ROUTES,
                    CONTENT_SUITE_ENTRY_ROUTES,
                  ],
                },
                HELP_DOCS_ROUTES,
                HELP_DOCS_ARTICLE_ROUTES,
                UPDATES_ROUTES,
                UPDATES_HELP_DOCS_ROUTES,
                UPDATES_HELP_DOC_ROUTES,
                UPDATES_NOTE_ROUTES,
                ASSISTANT_ROUTES,
                CAPITAL_ROUTES,

                // Site-level settings routes with dedicated guard
                {
                  element: <SettingsGuard />,
                  children: [
                    SETTINGS_ROUTES,
                    LOCATION_SETTINGS_ROUTES,
                    LOCATION_SETTINGS_TABS_ROUTES,
                    // OC-320: Spot-based booking (Room Resource Configuration) is Premium — route-gate
                    // the whole page so typing the URL is blocked, not just the entry row.
                    {
                      element: <FeatureGatedRoute feature={FeatureKeys.SpotBasedBooking} />,
                      children: [ROOM_RESOURCE_CONFIGURATION_ROUTES],
                    },
                    LOCATION_COMMUNICATION_TEMPLATES_ROUTES,
                    SCHEDULER_SETTINGS_ROUTES,
                    SCHEDULER_SETTINGS_TABS_ROUTES,
                    RETAIL_SETTINGS_ROUTES,
                    RETAIL_SETTINGS_TABS_ROUTES,
                    ACCOUNT_SETTINGS_ROUTES,
                    ACCOUNT_SETTINGS_TABS_ROUTES,
                    WELLNESS_SETTINGS_ROUTES,
                    WELLNESS_SETTINGS_TABS_ROUTES,
                    // OC-759: SITE_AUDIT_HISTORY_ROUTES,
                  ],
                },
              ],
            },
          ],
        },
      ],
    },

    // Public routes (with app layout)
    DYNAMIC_LOADER_DEMO_ROUTES,
    VALENTINE_TEST_ROUTES,
    ERROR_BOUNDARY_TEST_ROUTES,

    // Catch all
    {
      path: '*',
      element: <CatchAllRoute />,
    },
  ],
}

const RESET_PASSWORD_ROUTES = {
  path: 'reset-password',
  element: withSuspense(ResetPasswordPage),
}

// Demo routes (public, no auth required — only served on demo.* subdomain)
// Routes are top-level (/signup, /verify, etc.) since the subdomain already scopes them
const DEMO_SIGNUP_ROUTES = { path: 'signup', element: withSuspense(DemoSignup) }
const DEMO_VERIFY_ROUTES = { path: 'verify', element: withSuspense(DemoVerify) }
const DEMO_CREATING_ROUTES = { path: 'creating', element: withSuspense(DemoCreating) }
const DEMO_MAGIC_LINK_ROUTES = { path: 'magic-link', element: withSuspense(DemoMagicLinkCallback) }

// Public routes without app layout (no sidebar/navigation)
// Each route gets its own error boundary so chunk-load failures auto-reload
const PUBLIC_ROUTES = [
  ACCEPT_INVITATION_ROUTES,
  WAITLIST_CONFIRM_ROUTES,
  COVER_CLAIM_ROUTES,
  PUBLIC_INVOICE_ROUTES,
  PUBLIC_AGREEMENT_ROUTES,
  PUBLIC_AGREEMENT_V2_ROUTES,
  PUBLIC_FORM_FILL_ROUTES,
  ...PUBLIC_PENDING_ITEMS_ROUTES,
  PUBLIC_PAYMENT_APPROVAL_ROUTES,
  BARCODE_CHECK_IN_ROUTES,
  RESET_PASSWORD_ROUTES,
  DEMO_SIGNUP_ROUTES,
  DEMO_VERIFY_ROUTES,
  DEMO_CREATING_ROUTES,
  DEMO_MAGIC_LINK_ROUTES,
].map((route) => ({ ...route, errorElement: <RootErrorBoundary /> }))

/** @type {import('react-router-dom').Router} */
const router = createBrowserRouter([...PUBLIC_ROUTES, APP_ROUTES])

export default router
